<template>
  <div class="ai-risk-assessment">
    <el-button
      type="primary"
      :loading="loading"
      @click="handleAiAssess"
      :disabled="disabled"
      size="mini"
    >
      {{ btnText }}
    </el-button>
    <span v-if="loading" class="ai-loading-text">正在AI评估中，请稍候...</span>
  </div>
</template>

<script>
/**
 * AI风险评估通用组件
 *
 * Props:
 *   formData      - 表单数据对象（用于读取 name 和写入评估结果）
 *   nameField     - 表单中表示物品名称的字段名，默认 'name'
 *   apiUrl        - AI评估接口路径，默认 'api/v2/BdBce/runs?obj_type=haocai'
 *   fieldMapping  - API返回字段到表单字段的映射关系
 *   btnText       - 按钮文字，默认 'AI风险评估'
 *   disabled      - 是否禁用按钮
 *
 * API返回示例:
 *   {
 *     "level": "Ⅱ级",
 *     "type": "爆炸/反应失控风险, 易燃易爆风险, 腐蚀风险",
 *     "store": "浸没在煤油或石蜡油中密封保存...",
 *     "protection": "实验手套、护目镜...",
 *     "measure": "皮肤接触：立即用干布擦去...",
 *     "is_hazardous": "是"
 *   }
 */
export default {
  name: 'AiRiskAssessment',
  props: {
    formData: {
      type: Object,
      required: true,
    },
    nameField: {
      type: String,
      default: 'name',
    },
    apiUrl: {
      type: String,
      default: 'api/v2/BdBce/runs?obj_type=haocai',
    },
    fieldMapping: {
      type: Object,
      default: () => ({
        level: 'fx_level',
        type: 'fx_type',
        store: 'fx_store',
        protection: 'fx_protection',
        measure: 'fx_measure',
      }),
    },
    btnText: {
      type: String,
      default: 'AI风险评估',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    async handleAiAssess() {
      const name = this.formData[this.nameField];
      if (!name || !name.trim()) {
        this.$message({
          type: 'warning',
          center: true,
          message: '请先输入物品名称',
        });
        return;
      }

      this.loading = true;
      try {
        const res = await this.$api.instance.interfaceServer(
          this.apiUrl,
          'post',
          { name: name.trim() }
        );

        if (res.code !== 0) {
          this.$message({
            type: 'error',
            center: true,
            message: res.msg || 'AI风险评估失败',
          });
          return;
        }

        // 实际数据在 res.data.answer 中，是 JSON 字符串，需要解析
        let riskData = null;
        if (res.data && res.data.answer) {
          try {
            riskData = typeof res.data.answer === 'string'
              ? JSON.parse(res.data.answer)
              : res.data.answer;
          } catch (e) {
            console.error('解析AI返回数据失败:', e);
            this.$message({ type: 'error', center: true, message: 'AI返回数据解析失败' });
            return;
          }
        }

        if (!riskData) {
          this.$message({ type: 'warning', center: true, message: '未获取到风险评估数据' });
          return;
        }

        // 遍历字段映射，填充表单
        Object.keys(this.fieldMapping).forEach((apiKey) => {
          const formKey = this.fieldMapping[apiKey];
          if (riskData[apiKey] !== undefined && riskData[apiKey] !== null) {
            // fx_type 是逗号分隔字符串，转为数组供多选使用
            if (formKey === 'fx_type' && typeof riskData[apiKey] === 'string') {
              this.$set(
                this.formData,
                formKey,
                riskData[apiKey].split(/[,，]/).map((s) => s.trim()).filter(Boolean)
              );
            } else {
              this.$set(this.formData, formKey, riskData[apiKey]);
            }
          }
        });

        this.$message({
          type: 'success',
          center: true,
          message: 'AI风险评估完成，已自动填充',
        });
      } catch (e) {
        console.error('AI风险评估失败:', e);
        this.$message({
          type: 'error',
          center: true,
          message: 'AI风险评估失败，请稍后重试',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.ai-risk-assessment {
  display: inline-block;
  margin-bottom: 8px;

  .ai-loading-text {
    margin-left: 8px;
    color: #409eff;
    font-size: 13px;
  }
}
</style>
